import * as THREE from 'three/webgpu'
import { OrbitControls } from 'three/addons/controls/OrbitControls.js'
import { mountMoodPicker, moods, currentMood } from './mood-picker.js'
import { Inspector } from 'three/addons/inspector/Inspector.js'
import { bloom } from 'three/examples/jsm/tsl/display/BloomNode.js'
import { color, cross, deltaTime, dot, float, Fn, hash, instancedArray, instanceIndex, mix, pass, time, TWO_PI, uniform, uv, vec3 } from 'three/tsl'
import { HINGE_PX, sampleHummingbird, toWorld } from './shape.js'
import { randomSphericalPosition } from './utils.js'

/**
 * Base
 */
const canvas = document.querySelector('canvas.threejs')
const params = new URLSearchParams(location.search)
const scene = new THREE.Scene()

const sizes = {
    width: window.innerWidth,
    height: window.innerHeight
}

window.addEventListener('resize', () =>
{
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight

    camera.aspect = sizes.width / sizes.height
    fitCamera()

    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
})

/**
 * Camera
 */
const camera = new THREE.PerspectiveCamera(35, sizes.width / sizes.height, 0.1, 100)
// Keep ~3.2 units of bird visible across the screen width (portrait phones need a farther camera)
const fitCamera = () =>
{
    const widthPerUnit = 2 * Math.tan(THREE.MathUtils.degToRad(camera.fov / 2)) * camera.aspect
    camera.position.setLength(Math.max(6, 3.2 / widthPerUnit))
    camera.updateProjectionMatrix()
}
camera.position.set(0, 0, 6)
fitCamera()
scene.add(camera)

const controls = new OrbitControls(camera, canvas)
controls.enableDamping = true
controls.enablePan = false
const touch = window.matchMedia('(pointer: coarse)').matches
if(touch) controls.enableRotate = false

/**
 * Renderer
 */
// Falls back to WebGL 2 by itself when the browser has no WebGPU; ?webgl=1 forces it to test that path
const renderer = new THREE.WebGPURenderer({ canvas, antialias: true, forceWebGL: params.get('webgl') === '1' })
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
renderer.setClearColor(0x0b1020)
const debug = params.get('debug') === '1'
if(debug) renderer.inspector = new Inspector()
// Without ?debug=1 the GUI calls go to a silent stand-in, so the panel code stays in one place
const noopControl = { name: () => noopControl }
const noopFolder = { add: () => noopControl, addColor: () => noopControl, close: () => noopFolder }
const gui = debug ? renderer.inspector : { createParameters: () => noopFolder }
const { report } = window.mobitaDiag
const fallbackToWebGL = (reason) =>
{
    // One automatic retry on WebGL 2 per tab; after that we only report
    let retried = false
    try { retried = sessionStorage.getItem('mobita-webgl-retry') === '1'; sessionStorage.setItem('mobita-webgl-retry', '1') } catch {}
    report('fallback', { reason, retried })
    if(retried || params.get('webgl') === '1') return
    params.set('webgl', '1')
    location.replace(`${location.pathname}?${params}`)
}
renderer.onError = (info) => report('gpu-error', { type: info.type, message: info.message })
renderer.onDeviceLost = (info) => { report('device-lost', { reason: info.reason, message: info.message }); fallbackToWebGL('device-lost') }
// iOS sessions were seen stuck before the backend was ready: give init 6 s, then retry on WebGL 2
const initTimeout = setTimeout(() => { report('init-timeout'); fallbackToWebGL('init-timeout') }, 6000)
try { await renderer.init() }
catch(error)
{
    // No WebGPU and no usable WebGL (headless bots, very old devices): show the still bird instead of a blank page
    clearTimeout(initTimeout)
    report('no-gpu', { message: String(error?.message || error) })
    document.body.classList.add('static')
    throw error
}
clearTimeout(initTimeout)
const backend = renderer.backend.isWebGPUBackend ? 'WebGPU' : 'WebGL 2'
// Read the adapter from the device three already created; a second requestAdapter() can stall WebKit
const info = renderer.backend.device?.adapterInfo
const adapter = info ? `${info.vendor} ${info.architecture} ${info.description || ''}`.trim() : null
report('backend', { backend, adapter })

/**
 * Post processing
 */
const renderPipeline = new THREE.RenderPipeline(renderer)
const scenePassColor = pass(scene, camera).getTextureNode('output')
const bloomPass = bloom(scenePassColor)
bloomPass.threshold.value = 0
bloomPass.strength.value = 0.35
// Bloom drops WebKit's WebGL 2 path to ~10 fps (Chrome on iPhone has no WebGPU and lands there): WebGPU only.
// Measured 2026-10-19 in Safari ?webgl=1: 13 fps with bloom, 66 fps without, at any particle count.
const useBloom = backend === 'WebGPU' && params.get('bloom') !== '0'
renderPipeline.outputNode = useBloom ? scenePassColor.add(bloomPass) : scenePassColor

const bloomGui = gui.createParameters('Bloom').close()
bloomGui.add(bloomPass.threshold, 'value', 0, 2, 0.01).name('threshold')
bloomGui.add(bloomPass.strength, 'value', 0, 2, 0.01).name('strength')

/**
 * Cursor
 */
const raycaster = new THREE.Raycaster()
const cursor = new THREE.Vector2(10, 10) // off-screen until the pointer moves
const intersect = new THREE.Vector3()

window.addEventListener('pointermove', (event) =>
{
    cursor.x = (event.clientX / sizes.width) * 2 - 1
    cursor.y = - (event.clientY / sizes.height) * 2 + 1
})

// A click (not a drag) scatters the bird; it rebuilds by itself
let pointerDown = null
canvas.addEventListener('pointerdown', (event) => { pointerDown = [event.clientX, event.clientY] })
canvas.addEventListener('pointerup', (event) =>
{
    if(pointerDown && Math.hypot(event.clientX - pointerDown[0], event.clientY - pointerDown[1]) < 5)
        scatter()
    pointerDown = null
    if(event.pointerType === 'touch') cursor.set(10, 10)
})

const scatter = () =>
{
    burst.value = burstStrength.value
    rebuild.value = 0
}

/**
 * Hummingbird particles
 */
// ?count= and ?bloom=0 exist to measure what slows a device down (see the health event in the logs)
const count = Math.min(60000, Math.max(1000, Number(params.get('count')) || 30000))
const shapeData = await sampleHummingbird('./colibri.png', count)

const [hingeX, hingeY] = toWorld(HINGE_PX)
let wingLengthValue = 0
for(let i = 0; i < count; i++)
    if(shapeData[i * 4 + 3] === 1)
        wingLengthValue = Math.max(wingLengthValue, Math.hypot(shapeData[i * 4] - hingeX, shapeData[i * 4 + 1] - hingeY))

// Buffers
const shapeBuffer = instancedArray(shapeData, 'vec4') // xyz target + w: 1 = wing
const positionsBuffer = instancedArray(count, 'vec3')
const velocitiesBuffer = instancedArray(count, 'vec3')

// Uniforms
const hinge = uniform(new THREE.Vector3(hingeX, hingeY, 0))
const wingLength = uniform(wingLengthValue)
const bodyAxis = uniform(new THREE.Vector3(0.61, 0.79, 0).normalize()) // tail -> head
const flapFrequency = uniform(9)
const flapAngle = uniform(0.45)
const flapRoll = uniform(0.9)
const hoverAmplitude = uniform(0.035)
const stiffness = uniform(0.05)
const damping = uniform(0.14)
const cursorPosition = uniform(new THREE.Vector3(100, 100, 0))
const cursorVelocity = uniform(new THREE.Vector3())
const cursorRadius = uniform(0.45)
const cursorStrength = uniform(0.12)
const cursorRepel = uniform(0.02)
const burst = uniform(0)
const burstStrength = uniform(0.3)
const rebuild = uniform(0) // 0 → 1: how strongly particles pull back to the shape (ramps after a burst)
const rebuildSeconds = { value: 2.2 }
const size = uniform(0.016)
const opacity = uniform(0.55)
const glowStrength = uniform(25)
const colorBody = uniform(color(0xeaf0ff))
const colorWing = uniform(color(0xa9c2ff))
const colorFast = uniform(color(0x2f6bff))

// Rodrigues rotation of v around a unit axis
const rotateAround = Fn(([v, axis, angle]) =>
{
    const c = angle.cos()
    const s = angle.sin()
    return v.mul(c).add(cross(axis, v).mul(s)).add(axis.mul(dot(axis, v)).mul(c.oneMinus()))
}, { v: 'vec3', axis: 'vec3', angle: 'float', return: 'vec3' })

// Init: particles start scattered and assemble into the bird
const initCompute = Fn(() =>
{
    positionsBuffer.element(instanceIndex).assign(randomSphericalPosition(instanceIndex, hash(instanceIndex.add(5)).mul(4).add(2)))
})().compute(count)
renderer.compute(initCompute)

const updateCompute = Fn(() =>
{
    // Frame-rate independent steps expressed at 60 fps
    const k = deltaTime.min(1 / 30).mul(60)

    const shape = shapeBuffer.element(instanceIndex)
    const position = positionsBuffer.element(instanceIndex)
    const velocity = velocitiesBuffer.element(instanceIndex)

    // Wing flap: swing in the image plane + roll around the body axis (figure-8 feel)
    const local = shape.xyz.sub(hinge)
    const reach = local.length().div(wingLength).clamp(0, 1)
    const phase = time.mul(flapFrequency).mul(TWO_PI)
    const swing = phase.sin().mul(flapAngle).mul(reach.mul(0.4).add(0.6))
    const roll = phase.cos().mul(flapRoll)
    const flapped = rotateAround(rotateAround(local, vec3(0, 0, 1), swing), bodyAxis, roll).add(hinge)

    const hover = vec3(0, time.mul(1.7).sin().mul(hoverAmplitude), 0)
    const target = mix(shape.xyz, flapped, shape.w).add(hover)

    // Spring back to the shape
    velocity.addAssign(target.sub(position).mul(stiffness).mul(rebuild.pow(2)).mul(k))

    // Cursor: drag along with its motion + soft repulsion
    const toParticle = position.sub(cursorPosition)
    const distance = toParticle.length()
    const ratio = distance.div(cursorRadius).oneMinus().max(0)
    velocity.addAssign(cursorVelocity.mul(ratio).mul(cursorStrength))
    velocity.addAssign(toParticle.div(distance.max(0.0001)).mul(ratio.mul(ratio)).mul(cursorRepel).mul(k))

    // Click burst
    velocity.addAssign(randomSphericalPosition(instanceIndex, 1).mul(burst).mul(hash(instanceIndex.add(7)).mul(0.8).add(0.2)))

    position.addAssign(velocity.mul(k))
    velocity.mulAssign(damping.mul(k).oneMinus().max(0))
})().compute(count)

// Material
const material = new THREE.SpriteNodeMaterial({
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending
})
material.positionNode = positionsBuffer.toAttribute()
material.scaleNode = size.mul(hash(instanceIndex.add(3)).mul(0.7).add(0.5))

const speed = velocitiesBuffer.toAttribute().length()
const glow = speed.mul(glowStrength).clamp(0, 1)
const wingTint = shapeBuffer.toAttribute().w
material.colorNode = mix(mix(colorBody, colorWing, wingTint), colorFast, glow).toVertexStage()
material.opacityNode = float(1).sub(uv().sub(0.5).length().smoothstep(0.15, 0.5)).mul(opacity)

const particles = new THREE.Sprite(material)
particles.count = count
particles.frustumCulled = false
scene.add(particles)

// Debug
const wingsGui = gui.createParameters('Alas')
wingsGui.add(flapFrequency, 'value', 0, 30, 0.1).name('frecuencia (Hz)')
wingsGui.add(flapAngle, 'value', 0, 1.5, 0.01).name('barrido')
wingsGui.add(flapRoll, 'value', 0, 1.5, 0.01).name('giro')
wingsGui.add(hoverAmplitude, 'value', 0, 0.2, 0.001).name('flotación')

const physicsGui = gui.createParameters('Física')
physicsGui.add(stiffness, 'value', 0.001, 0.3, 0.001).name('resorte')
physicsGui.add(damping, 'value', 0.01, 0.5, 0.001).name('freno')
physicsGui.add(cursorRadius, 'value', 0, 2, 0.01).name('radio cursor')
physicsGui.add(cursorStrength, 'value', 0, 0.5, 0.001).name('arrastre cursor')
physicsGui.add(cursorRepel, 'value', 0, 0.2, 0.001).name('repulsión cursor')
physicsGui.add(burstStrength, 'value', 0, 1, 0.01).name('fuerza clic')
physicsGui.add(rebuildSeconds, 'value', 0.2, 6, 0.1).name('segundos en rearmarse')

const lookGui = gui.createParameters('Partículas').close()
lookGui.add(size, 'value', 0.002, 0.06, 0.001).name('tamaño')
lookGui.add(opacity, 'value', 0, 1, 0.01).name('opacidad')
lookGui.add(glowStrength, 'value', 0, 100, 0.1).name('brillo por velocidad')
lookGui.addColor(colorBody, 'value').name('cuerpo')
lookGui.addColor(colorWing, 'value').name('alas')
lookGui.addColor(colorFast, 'value').name('velocidad')

/**
 * Themes: dark glows (additive + bloom); light needs normal blending and ink particles
 */
const themes = {
    oscuro: { background: 0x0b1020, page: '#0B1020', hint: '#C9CED8', blending: THREE.AdditiveBlending, opacity: 0.55, size: 0.016, bloom: 0.35, body: 0xeaf0ff, wing: 0xa9c2ff, fast: 0x2f6bff },
    claro: { background: 0xf6f7f9, page: '#F6F7F9', hint: '#3F3F3F', blending: THREE.NormalBlending, opacity: 0.95, size: 0.021, bloom: 0, body: 0x0b1020, wing: 0x1e3a8a, fast: 0x0055ff }
}
const themeButton = document.querySelector('.theme')
const applyTheme = (name) =>
{
    const theme = themes[name]
    renderer.setClearColor(theme.background)
    document.body.style.background = theme.page
    document.querySelector('.hint').style.color = theme.hint
    themeButton.textContent = name === 'oscuro' ? 'Versión clara' : 'Versión oscura'
    themeButton.dataset.theme = name
    material.blending = theme.blending
    material.needsUpdate = true
    opacity.value = theme.opacity
    size.value = theme.size
    bloomPass.strength.value = theme.bloom
    // Without bloom the dark theme loses its glow: compensate with brighter particles
    if(!useBloom) opacity.value = Math.min(1, theme.opacity * (name === 'oscuro' ? 1.35 : 1))
    colorBody.value.set(theme.body)
    paintMood(name)
}
// The mood picker recolours wings and fast particles: on dark the tone made for ink, on light the solid accent
let mood = moods[currentMood()]
const paintMood = (themeName) =>
{
    colorWing.value.set(themeName === 'claro' ? mood.solid : mood.rim)
    colorFast.value.set(mood.dot)
}
const toggleTheme = () => applyTheme(themeButton.dataset.theme === 'oscuro' ? 'claro' : 'oscuro')
themeButton.addEventListener('click', toggleTheme)
window.addEventListener('keydown', (event) => { if(event.key === 't' || event.key === 'T') toggleTheme() })
if(touch) document.querySelector('.hint').textContent = 'Pasá el dedo para desarmarlo · tocá para dispersarlo'
applyTheme(params.has('claro') ? 'claro' : 'oscuro')
mountMoodPicker((picked) =>
{
    mood = picked
    paintMood(themeButton.dataset.theme)
})

/**
 * Animate
 */
const clock = new THREE.Timer()
const tick = () =>
{
    clock.update()
    rebuild.value = Math.min(1, rebuild.value + clock.getDelta() / rebuildSeconds.value)
    controls.update()

    // Cursor on the camera-facing plane through the bird
    raycaster.setFromCamera(cursor, camera)
    const plane = new THREE.Plane(camera.position.clone().normalize(), 0)
    if(raycaster.ray.intersectPlane(plane, intersect))
    {
        cursorVelocity.value.copy(intersect).sub(cursorPosition.value)
        if(cursorVelocity.value.length() > 1) cursorVelocity.value.set(0, 0, 0) // first frame / jump
        cursorPosition.value.copy(intersect)
    }

    renderer.compute(updateCompute)
    burst.value = 0

    renderPipeline.render()
}

// iOS (WebKit) sometimes shows nothing until the particle pipeline is rebuilt — toggling the theme fixed it.
// So: compile everything before the first frame, and rebuild the particle material once after start.
try { await renderer.compileAsync(scene, camera) } catch(error) { report('compile-failed', { message: String(error?.message || error) }) }
renderer.setAnimationLoop(tick)
setTimeout(() => { material.needsUpdate = true; report('material-refresh') }, 1000)

// Health check: after the intro the particles must be finite and inside the bird's frame, and frames must flow
let frames = 0
const countFrames = () => { frames++; requestAnimationFrame(countFrames) }
requestAnimationFrame(countFrames)
setTimeout(async () =>
{
    const fps = Math.round(frames / 4)
    const visible = document.visibilityState === 'visible'
    let finite = 0, inside = 0, total = 0
    try
    {
        const values = new Float32Array(await renderer.getArrayBufferAsync(positionsBuffer.value))
        const stride = values.length / count // 4 on WebGPU (vec3 padded), 3 on WebGL
        for(let i = 0; i < count; i += 97) // sample ~300 particles
        {
            const x = values[i * stride]
            const y = values[i * stride + 1]
            total++
            if(Number.isFinite(x) && Number.isFinite(y)) finite++
            if(Math.abs(x) < 2 && Math.abs(y) < 2) inside++
        }
    }
    catch(error) { report('health-read-failed', { message: String(error?.message || error) }) }
    // A hidden tab throttles frames: only judge speed when the page is on screen
    const healthy = total > 0 && finite === total && inside / total > 0.5 && (fps > 5 || !visible)
    report('health', { backend, count, bloom: useBloom, fps, visible, finite, inside, total, healthy })
    if(!healthy && backend === 'WebGPU') fallbackToWebGL('unhealthy')
}, 4000)

// Console access for tuning: window.mobita.flapFrequency.value = 2
window.mobita = { applyTheme, scatter, rebuild, flapFrequency, flapAngle, flapRoll, stiffness, damping, cursorRadius, cursorStrength, cursorRepel, burst, burstStrength, size, opacity, glowStrength, bloomPass }
